import React, { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { newReportConfig } from "../mdmReportsUtils";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import "../components/HierarchyDropdownContent.css";
import SearchIcon from "@mui/icons-material/Search";

interface SalesHierarchyFilterProps {
  selectedReport: newReportConfig;
  filters: Record<string, string[]>;
  optionsMap: Record<string, { label: string; value: string }[]>;
  loadingMap: Record<string, boolean>;
  onLevelChange: (value: string | null) => void;
  onValueChange: (values: string[]) => void;
  onLoadLevels: () => void;
  onNextLevel?: () => void;
  onDropdownClose?: () => void;
  onResetAll?: () => void;
}

const SalesHierarchyFilter: React.FC<SalesHierarchyFilterProps> = ({
  selectedReport,
  filters,
  optionsMap,
  loadingMap,
  onLevelChange,
  onValueChange,
  onLoadLevels,
  onNextLevel,
  onDropdownClose,
  onResetAll,
}) => {
  const isEnabled = !!selectedReport?.salesHierarchyFilter?.enabled && !!selectedReport?.salesHierarchyFilter;

  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    if (!isEnabled) return;
    console.log('[SalesHierarchyFilter] useEffect - onLoadLevels called');
    onLoadLevels();
  }, [isEnabled, onLoadLevels]);

  const currentLevel = selectedReport?.salesHierarchyFilter
    ? filters[selectedReport.salesHierarchyFilter.levelFilterField]?.[0]
    : undefined;
  const valueKey = currentLevel || '';
  const currentValues = filters[valueKey] || [];
  const hierarchyOrder = selectedReport?.salesHierarchyFilter?.hierarchyOrder || [];
  
  // DEBUG: Log filter state when component renders
  console.log('[SalesHierarchyFilter] Render:', {
    currentLevel,
    valueKey,
    currentValues,
    allFilters: filters,
    levelFilterField: selectedReport?.salesHierarchyFilter?.levelFilterField,
    hierarchyOrder
  });
  const lastLevel = hierarchyOrder.length > 0 ? hierarchyOrder[hierarchyOrder.length - 1] : null;
  const isLastLevel = currentLevel && currentLevel === lastLevel;

  const levelOptions = useMemo(() => {
    if (!selectedReport?.salesHierarchyFilter) return [];
    if (hierarchyOrder.length > 0) {
      // Filter out 'supplier' from UI while keeping it in config
      return hierarchyOrder.filter(level => level !== 'supplier');
    }
    const fallback = optionsMap[selectedReport.salesHierarchyFilter.levelFilterField] || [];
    // Filter out 'supplier' from fallback options as well
    return fallback.map(option => option.value).filter(level => level !== 'supplier');
  }, [hierarchyOrder, optionsMap, selectedReport?.salesHierarchyFilter]);

  const options = useMemo(() => optionsMap[valueKey] || [], [optionsMap, valueKey]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return options;
    }
    const lowered = searchTerm.toLowerCase();
    return options.filter(option => option.label.toLowerCase().includes(lowered));
  }, [options, searchTerm]);

  const allOptionValues = useMemo(() => options.map(option => option.value), [options]);
  const isAllSelected = useMemo(() => {
    if (allOptionValues.length === 0) return false;
    return allOptionValues.every(value => currentValues.includes(value));
  }, [allOptionValues, currentValues]);

  const handleOptionToggle = (optionValue: string) => {
    if (!currentLevel) return;
    console.log('[SalesHierarchyFilter] handleOptionToggle:', {
      optionValue,
      currentLevel,
      currentValues,
      isSelected: currentValues.includes(optionValue)
    });
    let updatedValues: string[];
    if (currentValues.includes(optionValue)) {
      updatedValues = currentValues.filter(value => value !== optionValue);
    } else {
      updatedValues = [...currentValues, optionValue];
    }
    console.log('[SalesHierarchyFilter] Updated values:', updatedValues);
    onValueChange(updatedValues);
  };

  const handleReset = () => {
    setSearchTerm("");
    // Call the parent reset handler to clear all hierarchy selections
    if (onResetAll) {
      onResetAll();
    } else {
      // Fallback to clearing current level
      if (currentLevel) {
        onValueChange([]);
      }
      onLevelChange(null);
    }
  };

  const handleSelectAllToggle = () => {
    if (!currentLevel) return;
    if (isAllSelected) {
      onValueChange([]);
    } else {
      onValueChange(allOptionValues);
    }
  };

  const handleApply = () => {
    if (onNextLevel) {
      onNextLevel();
    }
    onDropdownClose?.();
  };

  if (!isEnabled) {
    return null;
  }

  const hasSelections = currentValues.length > 0;

  return (
    <div className="hrc-filter-dropdown-container">

      <div className="hrc-filter-search-container">
        <SearchIcon className="hrc-filter-search-icon" />
        <input
          className="hrc-filter-search-input"
          placeholder="Search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          disabled={!currentLevel}
        />
      </div>

      <div className="hrc-filter-options">
        <div className="hrc-filter-options-left">
          <SimpleBar className="hrc-filter-level-list">
            {levelOptions.map(level => (
              <div
                key={level}
                className={`hrc-filter-level ${currentLevel === level ? "is-active" : ""}`}
                onClick={() => {
                  console.log('[SalesHierarchyFilter] Level clicked:', {
                    clickedLevel: level,
                    currentLevel,
                    filtersAtLevel: filters[level],
                    allFilters: filters
                  });
                  onLevelChange(level);
                }}
              >
                {level}
              </div>
            ))}
          </SimpleBar>
        </div>

        <div className="hrc-filter-options-right">
          {!currentLevel ? (
            <div className="hrc-empty-state">Select a level to view users</div>
          ) : loadingMap[valueKey] ? (
            <div className="hrc-empty-state">
              <CircularProgress size={20} />
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="hrc-empty-state">No options available</div>
          ) : (
            <>
              <div className="hrc-filter-select-all">
                <label>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAllToggle}
                  />
                  <span style={{marginLeft: "8px", paddingBottom: "2px"}}>Select all</span>
                </label>
              </div>
              <SimpleBar className="hrc-filter-option-list">
                {filteredOptions.map(option => {
                  const isChecked = currentValues.includes(option.value);
                  // DEBUG: Log checkbox state
                  if (filteredOptions.length <= 10) { // Only log for small lists to avoid spam
                    console.log('[SalesHierarchyFilter] Checkbox render:', {
                      optionValue: option.value,
                      optionLabel: option.label,
                      isChecked,
                      currentValues
                    });
                  }
                  return (
                    <label key={option.value} className="hrc-filter-option">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleOptionToggle(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </SimpleBar>
            </>
          )}
        </div>
      </div>

      <div className="hrc-filter-actions">
        <button className="hrc-btn hrc-btn--secondary" onClick={handleReset} style={{ fontSize: "13px", borderRadius: "8px", padding: "4px 12px" }}>
          Reset
        </button>
        <button
          className="hrc-btn hrc-btn--primary"
          onClick={handleApply}
          disabled={!currentLevel}
          style={{ fontSize: "13px", borderRadius: "8px", padding: "4px 12px" }}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default SalesHierarchyFilter;

